import React from 'react';
import {PageLayout} from '../../Layouts/PageLayout';
import {logout, UserApp} from '../../Context/AuthProvider/AuthProviderActions';
import {getUsersForMigrations, migrateUsers} from '../../Actions/Users/UsersActions';
import {useParameterizedQuery} from 'react-fetching-library';
import DataGrid, {Column, DataGridTypes, FilterRow, HeaderFilter, Paging, Search, DataGridRef} from 'devextreme-react/data-grid';
import {Button, Spin, Tooltip, Tour, TourStepProps} from 'antd';
import {ClearOutlined, ExclamationCircleOutlined, InteractionOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import {EditColumn} from '../../Components/DevExtream/DataGrid/EditColumn';
import {mailRegex, phoneRegex} from '../../Config/regex';
import {useAlertDispatcherState} from '../../Context/AlertProvider/AlertProvider';
import {addAlert} from '../../Context/AlertProvider/AlertProviderActions';
import {NewUserRequest} from '../../Actions/Users/UsersDomain';
import {useAuthDispatch, useAuthState} from '../../Context/AuthProvider/AuthProvider';
import modal from 'antd/lib/modal';
import {PageTitle} from '../../Components/PageTitle';
import {useLayoutState} from '../../Context/LayoutProvider/LayoutProvider';


export const MigrationTable: React.FunctionComponent = (_props) => {
    const [users, setUsers] = React.useState<UserApp[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const getUsersForMigrationsAction = useParameterizedQuery(getUsersForMigrations);
    const migrateUsersAction = useParameterizedQuery(migrateUsers);

    const gridRef = React.useRef<DataGridRef | null>(null);

    const alertDispatcher = useAlertDispatcherState();
    const authDispatcher = useAuthDispatch();

    const [openTour, setOpenTour] = React.useState(false);


    const {company} = useAuthState();

    const {isSmallScreen} = useLayoutState();


    React.useEffect(() => {
        if(!isLoading){
            setIsLoading(true);
            getUsersForMigrationsAction.query({}).then( res => {
                if (res.payload){
                    setUsers(res.payload.response);
                }
            } );
        }
    }, [getUsersForMigrationsAction, isLoading]);

    const checkIsValidData = React.useMemo(() => {
            const rows = users;
            let isValid = true;

            for (const rowData of rows) {
                if (!mailRegex.test(rowData.email)) {
                    isValid = false;
                    break;
                }
                if (rowData.telephone !== undefined && rowData.telephone.trim() !== '') {
                    const clearTelephone = rowData.telephone.replace(/\s/g, '').trim();
                    if (!phoneRegex.test(clearTelephone)) {
                        isValid = false;
                        break;
                    }
                }
            }

            return isValid;
        },
        [users]);

    React.useEffect( ()=>{
        if( users.length > 0 ){
            if(!checkIsValidData){
                addAlert('', 'Existen errores en los datos de migración, revice porfavor', 'error', alertDispatcher);
            }else{
                addAlert('', 'Los datos de migración son válidos, la empresa esta lista para migrar sus usuarios', 'success', alertDispatcher);
            }
        }
    }, [alertDispatcher, checkIsValidData, users] );

    const handleRowUpdating = React.useCallback( (e: DataGridTypes.RowUpdatingEvent) => {
        const updatedData = e.newData;
        const key = e.key;

        setUsers((prevUsers) =>
            prevUsers.map((user) =>
                user.id === key ? { ...user, ...updatedData } : user
            )
        );

        console.log('Row updated:', key, updatedData);
    }, []);

    const handleCellDblClick = React.useCallback(  (e: DataGridTypes.CellClickEvent|DataGridTypes.EditingStartEvent) => {
        let dataField = e.column && e.column.dataField ? e.column.dataField : 'email';
        if(!( dataField === 'email' || dataField === 'telephone' )){
            dataField = 'email';
        }
        const columnIndex = e.component.getVisibleColumns().findIndex((col: DataGridTypes.Column) => col.dataField === dataField);

        if ('rowIndex' in e) {
            e.component.editRow(e.rowIndex);
        }
        setTimeout(() => {
            const selector = `.dx-data-row td[aria-colindex="${columnIndex + 1}"] .dx-texteditor-input`;
            // buscamos el elemento del datagrid que tenga la clase dx-texteditor-input
            const inputElement = e.component.element().querySelector(selector);
            console.log(selector, inputElement);
            if (inputElement instanceof HTMLInputElement) {
                inputElement.focus();
                inputElement.select();
            }
        }, 100);
    }, []);

    const handleRowValidating =  React.useCallback( (e: DataGridTypes.RowValidatingEvent) => {
        const emailColumn = e.newData.email;
        if (emailColumn && !mailRegex.test(emailColumn)) {
            e.isValid = false;
            e.errorText = 'El correo electrónico no es válido';
        }

        const telephoneColumn = e.newData.telephone;
        if (telephoneColumn && telephoneColumn.trim() !== '' && !phoneRegex.test(telephoneColumn)) {
            e.isValid = false;
            e.errorText = 'El número de teléfono no es válido';
        }
    }, []);

    const formatPhoneNumber = React.useCallback((phoneNumber: string) => {
        if (!phoneNumber) return '';
        if (phoneNumber.startsWith('+')) {
            return phoneNumber.replace(/(\+\d{1,3})(\d{2})(\d{4})(\d{4})/, '($1) $2-$3-$4');
        }
        return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }, []);

    const clearAllFilters = React.useCallback( () => {
        if (gridRef.current) {
            const grid = gridRef?.current?.instance();

            if (grid){
                grid.clearFilter();
            }
        }
    }, []);

    const handdleMigrateUsers = React.useCallback( () => {
        if (checkIsValidData){
            const usersForMigration = users.map( user => {
                const newUser : NewUserRequest = {
                    email: user.email,
                    first_names: user.first_name,
                    last_names: user.last_name,
                    ids: user.ids,
                };

                if( user.telephone !== undefined && user.telephone.trim() !== '' ){
                    newUser.telephone =  user.telephone.replace(/\s/g, '').trim();
                }

                return newUser;
            } );

            migrateUsersAction.query({users: usersForMigration}).then( res => {
                if (res.payload){
                    addAlert( 'Migración de usuarios exitosa', res.payload.response, 'success', alertDispatcher);
                    logout(authDispatcher);
                }
            } );
        }
    }, [alertDispatcher, authDispatcher, checkIsValidData, migrateUsersAction, users]);

    const steps : TourStepProps[]  = React.useMemo(()=>{
        if ( !isLoading || getUsersForMigrationsAction.loading ) {
            return [] as TourStepProps[];
        }

        const gridElement = gridRef.current?.instance()?.element();

        return [
            {
                title: 'Bienvenido 🎉',
                description: '"¡Bienvenido al proceso de migración! Aquí puedes gestionar la activación de cuentas para los usuarios de tu empresa. Sigue los pasos para completar la migración con éxito."',
                //target: rowsView ? () => rowsView : undefined,
            },
            {
                title: 'Completa y verifica los correos',
                description: 'Debes ingresar o verificar el correo electrónico de cada usuario. Sin un correo válido, no podrán acceder a las plataformas.',
                target: ()=>{
                    return gridElement?.querySelector('.dx-header-row td .email-table-header');
                },
            },
            {
                title: 'Completa los teléfonos',
                description: 'Puedes agregar números de teléfono para recuperación de cuenta y soporte.',
                target: ()=>{
                    return gridElement?.querySelector('.dx-header-row td .telephone-table-header');
                },
            },
            {
                title: 'Migrar Usuarios 🚀',
                description: 'Cuando todos los correos estén completos, haz clic en Migrar Usuarios para activar las cuentas en las plataformas.',
                target: () => {
                    return document.querySelector('#migrate-users-btn') as HTMLElement;;
                },
            }
        ] as TourStepProps[];
    }, [isLoading, getUsersForMigrationsAction.loading]);

    const showModalConfirm = React.useCallback(() => {
        modal.confirm({
            title: 'Confirmación de Migración de usuarios',
            content: 'Estás a punto de migrar todos los usuarios a un nuevo “Inicio de Sesión”. Una vez completado, los usuarios recibirán un correo con sus credenciales. ¿Deseas continuar?"',
            okText: 'Sí, Migrar Usuarios',
            cancelText: 'Cancelar',
            onOk: () => {
                handdleMigrateUsers();
            },
            prefixCls:'finish-migration-modal',
            icon: <ExclamationCircleOutlined style={{ color: 'var(--warning-color)' }} />,
        });
    }, [handdleMigrateUsers]);

    const TITLE = React.useMemo(()=>{
        let migrationBtn = (
            <Button
                icon={ <InteractionOutlined /> }
                type="primary"
                disabled={ !checkIsValidData || migrateUsersAction.loading }
                onClick={ showModalConfirm }
                loading={ migrateUsersAction.loading }
                block={ isSmallScreen }
            >
                Migrar Usuarios
            </Button>
        );

        if (!checkIsValidData){
            migrationBtn = (<Tooltip title="Datos de migración inválidos">{ migrationBtn }</Tooltip>);
        }

        const buttons = [
            <div id="migrate-users-btn" key="migrate-users-btn">
                { migrationBtn }
            </div>,
            <Button
                color="primary"
                variant="outlined"
                icon={<ClearOutlined/>}
                onClick={clearAllFilters}
                block={ isSmallScreen }
                key="clear-filters-btn"
            >
                Limpiar Filtros
            </Button>
        ];


        return (
            <PageTitle title="Migración de Usuarios"
                       buttons={ buttons }
                       titleElement={(
                           <Tooltip title="Como Navegar">
                               <Button icon={<QuestionCircleOutlined />}
                                       onClick={ ()=>{
                                           setOpenTour(p => !p);
                                       } }
                                       type='link' disabled={steps.length === 0}
                               />
                           </Tooltip>
                       )}
                       forceOpenHamburgerButton={ openTour === true ? true : undefined }
            />
        );

    }, [checkIsValidData, migrateUsersAction.loading, showModalConfirm, isSmallScreen, clearAllFilters, steps.length, openTour]);


    React.useMemo( ()=>{
        const migrationsCompanies = localStorage.getItem('migration') ? JSON.parse( localStorage.getItem('migration') + '' ) : {};

        if( company !== undefined  ){
            if (!openTour ){
                if (migrationsCompanies[company.name] === undefined ) {
                    console.log(migrationsCompanies);
                    migrationsCompanies[company.name] = false;

                    localStorage.setItem('migration', JSON.stringify(migrationsCompanies));
                    setOpenTour(true);
                }else if (migrationsCompanies[company.name] === false) {
                    migrationsCompanies[company.name] = true;
                    localStorage.setItem('migration', JSON.stringify(migrationsCompanies));

                    setOpenTour(true);
                }
            }

        }

    }, [company, openTour]);


    return (
        <PageLayout title={ TITLE }
                    disableOtherItems
        >
            <Spin spinning={getUsersForMigrationsAction.loading} tip="Obteniendo usuarios...">
            <DataGrid
                ref={gridRef}
                dataSource={users}
                keyExpr="id"
                showBorders={false}
                className="datagrid-custom"
                onRowUpdating={handleRowUpdating}
                onCellDblClick={handleCellDblClick}
                onRowValidating={handleRowValidating}
                onEditingStart={handleCellDblClick}
                onEditorPreparing={(e) => {
                    if (e.dataField === 'telephone' && e.parentType === 'dataRow') {
                        if (e.value) {
                            e.editorOptions.value = e.value.replace(/\s+/g, '');
                        }
                    }
                }}
            >
                <HeaderFilter visible={true}>
                    <Search enabled={true}/>
                </HeaderFilter>
                <FilterRow visible={true}/>
                <Paging enabled={true}/>

                <Column dataField="ids"
                        caption="Llave"
                        allowEditing={false}
                        dataType="string"
                        cssClass="non-editable-cell"
                />
                <Column dataField="clave"
                        caption="Clave"
                        allowEditing={false}
                        dataType="string"
                        cssClass="non-editable-cell"
                />
                <Column dataField="first_name"
                        caption="Nombre"
                        allowEditing={false}
                        dataType="string"
                        cssClass="non-editable-cell"
                />
                <Column dataField="last_name"
                        caption="Apellido"
                        allowEditing={false}
                        dataType="string"
                        cssClass="non-editable-cell"
                />
                <Column
                    dataField="email"
                    caption="Correo Electrónico"
                    allowEditing={true}
                    dataType="string"
                    cssClass={'editable-cell'}
                    headerCellRender={() => <span className="email-table-header required-asterisk">Correo Electrónico</span>}
                    cellRender={(cellData) => {
                        const isValid = checkIsValidData || mailRegex.test(cellData.value);
                        let cell = (
                            <div className={!isValid ? 'has-validate-error' : undefined}>{cellData.value}</div>
                        );

                        if (!isValid){
                            cell = (
                                <Tooltip title="Correo electrónico inválido">
                                    <div className="has-validate-error">{cellData.value}</div>
                                </Tooltip>
                            );
                        }

                        return cell;
                    }}
                />
                <Column
                    dataField="telephone"
                    caption="Teléfono (Opcional)"
                    allowEditing={true}
                    headerCellRender={() => <span className="telephone-table-header">Teléfono (Opcional)</span>}
                    cellRender={(cellData) => {
                        const telephone = cellData.value;
                        const clearTelephone = telephone.replace(/\s/g, '').trim();

                        const telephoneIsEmpty = clearTelephone === undefined || clearTelephone.trim() === '';
                        const isValid = checkIsValidData || telephoneIsEmpty || phoneRegex.test(clearTelephone);

                        let cell = (
                            <div className={!isValid ? 'has-validate-error' : undefined}>
                                { formatPhoneNumber(cellData.value)}
                            </div>
                        );

                        if (!isValid){
                            cell = (
                                <Tooltip title="Teléfono inválido">{cell}</Tooltip>
                            );
                        }

                        return cell;
                    }}
                    dataType="string"
                    cssClass="editable-cell"
                />

                <EditColumn gridRef={gridRef}/>

            </DataGrid>
            </Spin>
            { steps.length > 0 && (
                <Tour open={openTour}
                      onClose={() => {
                          setOpenTour(false);
                      }}
                      type="primary"
                      steps={steps}
                      onChange={ (current: number) => {
                          console.log(current === steps.length - 1);
                      } }
                      rootClassName={'finish-migration-modal'}

                />
            )}
        </PageLayout>
    );
};
